import { tr } from '@/lib/i18n-core';
import { isEnrolmentKind, type PackageKind, type PackagePricing } from '@his/shared';
import { useAuth } from '@/lib/auth';
import { money } from '@/lib/utils';
import { DataTable } from '@/components/ui/layout';
import { Badge, StatusBadge } from '@/components/ui/primitives';

export interface ServicePackage {
  id: string;
  code: string;
  name: string;
  priceMinor: number;
  computedPriceMinor: number;
  pricing: PackagePricing;
  nphiesClaimable: boolean;
  inclusionsNote: string | null;
  exclusionsNote: string | null;
  billOverrun: boolean;
  kind: PackageKind;
  validityDays: number | null;
  allowanceMinor?: number | null;
  branchId: string | null;
  branch: { id: string; name: string } | null;
  isActive: boolean;
  saleChargeItem: { id: string; code: string; basePriceMinor: number; taxCategory: string | null; isActive: boolean } | null;
  items: { id: string; qty: number; chargeItem: { id: string; code: string; name: string; basePriceMinor: number; category?: string } }[];
}

export function packageKindLabel(kind: PackageKind): string {
  return tr('billing', `package_kind_${kind}`);
}

export function packageKindVariant(kind: PackageKind): 'secondary' | 'info' | 'success' | 'warning' {
  if (kind === 'health_checkup') return 'info';
  if (kind === 'ipd_stay') return 'warning';
  if (kind === 'session_bundle') return 'success';
  return 'secondary';
}

export interface PackageQuote {
  packageId: string;
  code: string;
  name: string;
  pricing: PackagePricing;
  contentsMinor: number;
  packageDiscountMinor: number;
  priceMinor: number;
  lines: {
    chargeItemId: string;
    code: string;
    description: string;
    qty: number;
    unitPriceMinor: number;
    grossMinor: number;
    discountMinor: number;
    packageDiscountMinor: number;
    taxMinor: number;
    netMinor: number;
  }[];
  totals: { grossMinor: number; discountMinor: number; taxMinor: number; netMinor: number };
}

export function packagePrice(pkg: Pick<ServicePackage, 'pricing' | 'priceMinor' | 'computedPriceMinor'>): number {
  return pkg.pricing === 'sum' ? pkg.computedPriceMinor : pkg.priceMinor;
}

export function PackagesTable({ rows, loading, error, onEdit }: { rows: ServicePackage[] | undefined; loading: boolean; error: unknown; onEdit?: (pkg: ServicePackage) => void }) {
  const { hasModule } = useAuth();
  const nphies = hasModule('insurance');
  return (
    <DataTable
      rows={rows}
      loading={loading}
      error={error}
      rowKey={(row) => row.id}
      onRowClick={onEdit}
      empty={tr('billing', 'no_packages_defined')}
      columns={[
        { header: tr('billing', 'code'), width: '130px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
        {
          header: tr('billing', 'package'),
          sort: (row) => row.name,
          cell: (row) => (
            <div className="min-w-0">
              <p className="truncate">{row.name}</p>
              {row.branch && <p className="truncate text-xs text-muted-foreground">{row.branch.name}</p>}
            </div>
          ),
        },
        {
          header: tr('billing', 'kind'),
          sort: (row) => row.kind,
          cell: (row) => <Badge variant={packageKindVariant(row.kind)}>{packageKindLabel(row.kind)}</Badge>,
        },
        { header: tr('billing', 'services'), align: 'right', sort: (row) => row.items.length, cell: (row) => row.items.length },
        { header: tr('billing', 'package_price'), align: 'right', sort: (row) => packagePrice(row), cell: (row) => money(packagePrice(row)) },
        {
          header: tr('billing', 'validity'),
          align: 'right',
          sort: (row) => row.validityDays ?? 0,
          cell: (row) =>
            row.validityDays ? tr('billing', 'x_days', { count: row.validityDays }) : row.kind === 'ipd_stay' ? tr('billing', 'one_admission') : '—',
        },
        ...(nphies
          ? [{ header: tr('billing', 'nphies'), sort: (row: ServicePackage) => row.nphiesClaimable, cell: (row: ServicePackage) => (row.nphiesClaimable ? <Badge variant="success">{tr('billing', 'nphies')}</Badge> : '—') }]
          : []),
        {
          header: tr('billing', 'beyond_the_package'),
          sort: (row) => row.billOverrun,
          cell: (row) => (
            <Badge variant={row.billOverrun ? 'secondary' : 'muted'}>
              {row.billOverrun ? tr('billing', 'billed_on_top') : isEnrolmentKind(row.kind) ? tr('billing', 'beyond_refused') : tr('billing', 'absorbed')}
            </Badge>
          ),
        },
        {
          header: tr('billing', 'status'),
          sort: (row) => row.isActive,
          cell: (row) => <StatusBadge status={row.isActive ? 'active' : 'inactive'} />,
        },
      ]}
    />
  );
}
